import { EllipsisVertical, Eye, Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { DocumentTypeSchema } from "@noa/api-core";
import { Button, Dropdown, Tag, Tooltip, DataTable, type DataTableColumn } from "~/components/ui";

type SchemaListProps = {
  schemas: DocumentTypeSchema[];
  isLoading: boolean;
  onView: (schema: DocumentTypeSchema) => void;
  onEdit: (schema: DocumentTypeSchema) => void;
  onDelete: (schema: DocumentTypeSchema) => void;
};

export function SchemaList({ schemas, isLoading, onView, onEdit, onDelete }: SchemaListProps) {
  const { t } = useTranslation("admin");

  const columns: DataTableColumn<DocumentTypeSchema>[] = [
    {
      title: t("schemaAdmin.columns.schemaId"),
      key: "schema_id",
      render: (record) => (
        <span>
          <code className="text-sm">{record.schema_id}</code>
          {record.is_builtin && (
            <Tag color="blue" className="ml-2">
              {t("schemaAdmin.builtin")}
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: t("schemaAdmin.columns.docType"),
      key: "doc_type",
      render: (record) => record.doc_type,
    },
    {
      title: t("schemaAdmin.columns.description"),
      key: "description",
      ellipsis: true,
      width: "30%",
      render: (record) => (
        <Tooltip title={record.description} placement="topLeft">
          <span>{record.description}</span>
        </Tooltip>
      ),
    },
    {
      title: t("schemaAdmin.columns.version"),
      key: "version",
      width: 80,
      align: "center",
      render: (record) => record.version,
    },
    {
      title: t("schemaAdmin.columns.fields"),
      key: "fields",
      width: 80,
      align: "center",
      render: (record) => record.fields?.length ?? 0,
    },
    {
      title: t("schemaAdmin.columns.actions"),
      key: "actions",
      width: 80,
      align: "center",
      render: (record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "view",
                icon: <Eye size={14} />,
                label: t("schemaAdmin.actions.view"),
                onClick: () => onView(record),
              },
              {
                key: "edit",
                icon: <Pencil size={14} />,
                label: t("schemaAdmin.actions.edit"),
                onClick: () => onEdit(record),
              },
              {
                type: "divider",
              },
              {
                key: "delete",
                icon: <Trash2 size={14} />,
                label: record.is_builtin ? (
                  <Tooltip title={t("schemaAdmin.deleteDisabledTooltip")}>
                    <span>{t("schemaAdmin.actions.delete")}</span>
                  </Tooltip>
                ) : (
                  t("schemaAdmin.actions.delete")
                ),
                danger: true,
                disabled: record.is_builtin,
                onClick: () => onDelete(record),
              },
            ],
          }}
          trigger={["click"]}
        >
          <Button variant="text" icon={<EllipsisVertical size={14} />} aria-label={t("schemaAdmin.actions.more")} />
        </Dropdown>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={schemas}
      rowKey={(schema) => schema.schema_id}
      loading={isLoading}
      pagination={{ pageSize: 20 }}
    />
  );
}
